import type { CityConfig } from "@/pages/city-template/types";

export const westHartfordConfig: CityConfig = {
  city: "West Hartford",
  state: "Connecticut",
  stateAbbr: "CT",
  slug: "west-hartford",
  route: "/west-hartford",
  title: "Expedited Passport Services in West Hartford, CT | Fast Passport Center",
  metaDescription:
    "Get your passport fast in West Hartford, CT. Fast Passport Center offers 24-hour to 10-day expedited passport processing. Registered courier serving West Hartford, Bloomfield, Farmington, and all of Greater Hartford.",
  h1: "Expedited Passport Services in West Hartford, CT",
  heroSubtitle:
    "West Hartford travelers trust Fast Passport Center for emergency and expedited passports — from 24-hour emergency turnaround to 10-day Smart Saver. No need to visit the Connecticut Passport Agency.",
  heroImageUrl:
    "https://readdy.ai/api/search-image?query=Golden%20hour%20view%20of%20West%20Hartford%20Connecticut%20Center%20with%20charming%20boutiques%20and%20restaurants%20along%20LaSalle%20Road%2C%20upscale%20New%20England%20town%20center%20with%20tree-lined%20streets%20and%20autumn%20foliage%2C%20warm%20amber%20lighting%2C%20professional%20travel%20photography%2C%20no%20text%20overlay%2C%20no%20people%20visible&width=1600&height=700&seq=ct-wh-hero-001&orientation=landscape",
  heroImageAlt: "West Hartford Center Connecticut at golden hour",
  geoPosition: "41.7620; -72.7417",
  geoPlacename: "West Hartford, Connecticut, United States",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "Connecticut Passport Agency",
    address: "850 Canal Street, Stamford, CT 06902",
    distance: "~120 miles from West Hartford",
  },
  nearbyCitiesZips: [
    { name: "West Hartford", zips: "06107, 06110, 06117, 06119, 06127, 06133, 06137" },
    { name: "Bloomfield", zips: "06002" },
    { name: "Farmington", zips: "06030, 06032, 06034" },
    { name: "Avon", zips: "06001" },
    { name: "Simsbury", zips: "06070, 06089, 06092" },
    { name: "Canton", zips: "06019, 06020" },
    { name: "Hartford", zips: "06101-06106, 06112, 06114, 06115, 06120, 06132, 06134, 06141, 06142, 06143, 06144, 06145, 06146, 06147, 06152, 06154, 06155, 06156, 06160, 06161, 06167, 06176, 06180, 06183, 06199" },
    { name: "East Hartford", zips: "06108, 06118, 06128, 06138" },
    { name: "New Britain", zips: "06050, 06051, 06052, 06053" },
    { name: "Manchester", zips: "06040, 06041, 06042, 06045" },
    { name: "Windsor", zips: "06006, 06095" },
    { name: "Wethersfield", zips: "06109" },
    { name: "Rocky Hill", zips: "06067" },
    { name: "Glastonbury", zips: "06033" },
    { name: "South Windsor", zips: "06074" },
    { name: "Enfield", zips: "06082, 06083" },
    { name: "Southington", zips: "06489, 06479" },
    { name: "Bristol", zips: "06010, 06011" },
    { name: "Newington", zips: "06111" },
    { name: "Cromwell", zips: "06416" },
    { name: "Berlin", zips: "06037" },
  ],
  benefits: [
    {
      icon: "ri-time-line",
      title: "As Fast as 24 Hours",
      desc: "Emergency processing available for West Hartford travelers with urgent departure dates.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Registered Courier",
      desc: "U.S. Department of State registered passport courier with reserved agency submission slots.",
    },
    {
      icon: "ri-customer-service-2-line",
      title: "Real Human Support",
      desc: "Dedicated agents guide you through every step — no chatbots, no automated runaround.",
    },
    {
      icon: "ri-map-pin-line",
      title: "Serving Greater Hartford",
      desc: "West Hartford, Bloomfield, Farmington, Avon, Simsbury, and all Greater Hartford communities.",
    },
  ],
  testimonials: [
    {
      name: "Anne R.",
      location: "West Hartford, CT",
      rating: 5,
      text: "I needed my passport renewed for a family trip to Europe in 5 days. Fast Passport Center delivered it in 3 days. Their West Hartford service is truly excellent.",
      service: "Passport Renewal",
    },
    {
      name: "George M.",
      location: "Farmington, CT",
      rating: 5,
      text: "Applied for my first passport through Fast Passport Center. They walked me through every step and I had it in 4 days. Farmington residents should definitely use them.",
      service: "New Passport",
    },
    {
      name: "Rebecca T.",
      location: "Bloomfield, CT",
      rating: 5,
      text: "My son needed a passport for a school trip to France. Fast Passport Center handled the child passport process and had it ready in 4 days. Bloomfield families trust them.",
      service: "Child Passport",
    },
    {
      name: "Christopher S.",
      location: "Avon, CT",
      rating: 5,
      text: "Lost my passport before a business trip to Japan. Fast Passport Center replaced it in 48 hours and saved my trip. Avon residents can count on them for emergencies.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "wh-1",
      name: "West Hartford Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "100 North Main Street",
      city: "West Hartford",
      state: "CT",
      zip: "06107",
      phone: "860-236-2541",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "West Hartford Center",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport acceptance services available by appointment. First-time applicants and those not eligible for mail-in renewal must apply in person.",
      icon: "ri-building-2-line",
    },
    {
      id: "wh-2",
      name: "West Hartford Town Hall",
      type: "Town Clerk / Passport Services",
      address: "50 South Main Street",
      city: "West Hartford",
      state: "CT",
      zip: "06107",
      phone: "860-561-7500",
      hours: "Mon-Fri 8:00 AM - 4:30 PM",
      distance: "West Hartford Town Center",
      website: "https://www.westhartfordct.gov/",
      websiteLabel: "West Hartford Town Hall",
      notes: "Provides passport application acceptance services. Appointment recommended for all passport services.",
      icon: "ri-government-line",
    },
    {
      id: "wh-3",
      name: "Farmington Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "2 Monteith Drive",
      city: "Farmington",
      state: "CT",
      zip: "06032",
      phone: "860-677-0477",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Farmington",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport photo services and application acceptance available. Appointment required for passport applications.",
      icon: "ri-file-shield-line",
    },
    {
      id: "wh-4",
      name: "Bloomfield Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "60 Tunxis Avenue",
      city: "Bloomfield",
      state: "CT",
      zip: "06002",
      phone: "860-242-3811",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Bloomfield",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-building-2-line",
    },
    {
      id: "wh-5",
      name: "Avon Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "13 West Main Street",
      city: "Avon",
      state: "CT",
      zip: "06001",
      phone: "860-673-1114",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Avon",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-file-shield-line",
    },
    {
      id: "wh-6",
      name: "Hartford Main Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "141 Weston Street",
      city: "Hartford",
      state: "CT",
      zip: "06120",
      phone: "860-714-6120",
      hours: "Mon-Fri 8:30 AM - 4:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Hartford",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-government-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get a passport in West Hartford, CT?",
      a: "Fast Passport Center offers processing options from 24 hours to 10 business days. For emergency travel within 2 weeks, we recommend our 3-5 day or 24-hour emergency service. Standard processing through the post office takes 8-11 weeks.",
    },
    {
      q: "Do I need to visit the Connecticut Passport Agency if I live in West Hartford?",
      a: "No — if you use Fast Passport Center, you don't need to visit the Connecticut Passport Agency at all. We handle the agency submission for you as a registered courier. The only reason to visit the agency directly is for a documented life-or-death emergency, and appointments are extremely difficult to obtain.",
    },
    {
      q: "Where can I get passport photos in West Hartford?",
      a: "Passport photos are available at CVS Pharmacy, Walgreens, UPS Store, FedEx Office, and Walmart locations throughout West Hartford, Farmington, and Bloomfield. We also offer photo review as part of our service to ensure your photos meet all State Department requirements before submission.",
    },
    {
      q: "Can I renew my passport by mail from West Hartford?",
      a: "Yes, if your most recent passport was issued when you were 16 or older, within the last 15 years, and is undamaged. Mail renewal takes 8-11 weeks. For faster renewal, Fast Passport Center can expedite the process to as little as 3-5 days.",
    },
    {
      q: "What documents do I need for a first-time passport in West Hartford?",
      a: "You'll need proof of U.S. citizenship (birth certificate or naturalization certificate), a government-issued photo ID, two passport photos, and payment for government fees. Fast Passport Center provides a complete checklist and reviews your documents before submission.",
    },
    {
      q: "Does Fast Passport Center have an office in West Hartford?",
      a: "Fast Passport Center operates as a remote courier service — we do not have a physical office in West Hartford. We serve West Hartford and all of Greater Hartford through our secure document shipping and agency submission process. Our headquarters are in Washington, DC.",
    },
    {
      q: "What is the nearest airport to West Hartford for international travel?",
      a: "Bradley International Airport (BDL) is the closest at ~14 miles. JFK International is ~120 miles, LaGuardia is ~105 miles, and Logan International is ~100 miles. Most West Hartford residents use Bradley for domestic and international travel.",
    },
    {
      q: "How much does expedited passport service cost in West Hartford?",
      a: "Government fees are $130-$165 for adult passports, plus a $60 expedited fee. Fast Passport Center's service fees vary by processing speed: 10-day Smart Saver, 3-5 day Expedited, or 24-hour Emergency. Call 877-253-0084 for a personalized quote.",
    },
    {
      q: "Do both parents need to be present for a child passport in West Hartford?",
      a: "Yes, both parents or legal guardians must appear with the minor. If one parent cannot attend, a notarized DS-3053 Statement of Consent is required. Fast Passport Center provides guidance on all parental consent documentation.",
    },
    {
      q: "Can I get a passport the same day in West Hartford?",
      a: "Same-day passports are only available at regional passport agencies for documented life-or-death emergencies with proof of travel. Fast Passport Center's 24-hour emergency service is the fastest option available through a private courier.",
    },
    {
      q: "What should I do if my passport is lost or stolen in West Hartford?",
      a: "Report it immediately using Form DS-64 and apply for a replacement using Form DS-11. Fast Passport Center can help you file the report and process your replacement passport in 3-5 days.",
    },
    {
      q: "Does West Hartford have a local passport agency?",
      a: "No, West Hartford does not have a regional passport agency. The nearest regional agency is the Connecticut Passport Agency at 10 Causeway St Suite 247, Boston, MA, approximately 120 miles away. Fast Passport Center serves as your courier to this agency.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — West Hartford", address: "various CVS locations in West Hartford", hours: "Mon-Sun, hours vary by location" },
    { name: "Walgreens — West Hartford", address: "various Walgreens locations in West Hartford", hours: "Mon-Sun, hours vary by location" },
    { name: "UPS Store — West Hartford", address: "various UPS Store locations in West Hartford", hours: "Mon-Fri 9:00 AM - 7:00 PM, Sat 10:00 AM - 4:00 PM" },
    { name: "FedEx Office — West Hartford", address: "various FedEx Office locations in West Hartford", hours: "Mon-Fri 8:00 AM - 9:00 PM, Sat-Sun 10:00 AM - 6:00 PM" },
    { name: "Walmart — West Hartford", address: "various Walmart locations in West Hartford", hours: "Mon-Sun 7:00 AM - 10:00 PM" },
  ],
  dmvLocations: [
    {
      name: "CT DMV — Wethersfield",
      address: "60 State Street, Wethersfield, CT 06109",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "Main Connecticut DMV office. Accepts walk-ins but appointments are strongly recommended.",
      featured: true,
    },
    {
      name: "CT DMV — Manchester",
      address: "303 Middle Turnpike W, Manchester, CT 06040",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "Full DMV services for license renewals and REAL ID applications.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "BDL",
      name: "Bradley International Airport",
      distance: "~14 miles from West Hartford",
      address: "Schoephoester Rd, Windsor Locks, CT 06096",
      note: "Connecticut's primary airport and closest to West Hartford. Offers domestic flights and international connections.",
      flightUrl: "https://www.bradleyairport.com/",
      flightLabel: "Bradley International",
      featured: true,
    },
    {
      code: "BOS",
      name: "Logan International Airport",
      distance: "~100 miles from West Hartford",
      address: "1 Harborside Dr, Boston, MA 02128",
      note: "Major international hub with direct flights to Europe, Asia, and worldwide destinations.",
      flightUrl: "https://www.massport.com/logan-airport",
      flightLabel: "Logan International",
      featured: false,
    },
    {
      code: "JFK",
      name: "JFK International Airport",
      distance: "~120 miles from West Hartford",
      address: "JFK Access Rd, Queens, NY 11430",
      note: "One of the busiest international airports in the world with flights to virtually every global destination.",
      flightUrl: "https://www.jfkairport.com/",
      flightLabel: "JFK International",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-passport-line",
      title: "Emergency Passport",
      description: "For West Hartford travelers with urgent departures within 24 hours to 2 weeks. Reserved agency submission with dedicated courier handling.",
      bullets: ["Processing as fast as 24 hours", "Dedicated agent assigned to your case", "Daily status updates"],
      badge: "Fastest",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Fast renewal for West Hartford residents with a valid passport issued within the last 15 years at age 16 or older.",
      bullets: ["3-5 day or 10-day options available", "No acceptance clerk visit required", "Secure overnight document return"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-user-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for West Hartford residents with step-by-step guidance through the acceptance clerk process.",
      bullets: ["Complete application review before submission", "Acceptance clerk appointment guidance", "3-5 day or 10-day processing options"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alarm-warning-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement service for West Hartford residents who have lost their passport or had it stolen.",
      bullets: ["DS-64 form guidance included", "Replacement in 3-5 days or 10 days", "Acceptance clerk visit required"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 in West Hartford, with parental consent guidance and acceptance clerk support.",
      bullets: ["Parental consent documentation review", "Both parents present or notarized consent", "3-5 day or 10-day processing available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal name change for West Hartford residents.",
      bullets: ["Marriage certificate or court order review", "Name change in 3-5 days or 10 days", "No clerk visit for most renewals"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for West Hartford travelers with immediate departure needs." },
    { name: "Passport Renewal", description: "Fast passport renewal for West Hartford residents with a recent valid passport." },
    { name: "New Passport", description: "First-time passport application assistance for West Hartford residents." },
    { name: "Lost or Stolen Passport", description: "Passport replacement for West Hartford residents who have lost their passport." },
    { name: "Child Passport", description: "Passport services for minors under 16 in West Hartford." },
    { name: "Name Change", description: "Passport name change service after marriage, divorce, or legal name change in West Hartford." },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Photos must be 2\"x2\" with a plain white or off-white background. Many West Hartford travelers get rejected at the acceptance clerk because their photos don't meet strict State Department specs.",
    },
    {
      icon: "ri-file-list-3-line",
      title: "Missing or Incorrect Forms",
      description: "Using the wrong form (DS-11 vs DS-82) or missing required fields can cause weeks of delays. We review every form before submission.",
    },
    {
      icon: "ri-calendar-line",
      title: "Waiting Too Long to Start",
      description: "Standard processing takes 8-11 weeks. Many West Hartford travelers wait until the last minute, leaving no buffer for problems.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Incorrect Payment Amount",
      description: "Government fees change frequently. Using an outdated check or wrong amount will cause your application to be returned.",
    },
    {
      icon: "ri-shield-line",
      title: "Damaged or Expired ID",
      description: "Your driver's license or state ID must be valid and unexpired. Many applicants don't realize their ID is about to expire until it's too late.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For child passports, both parents must appear or provide notarized consent. Missing documentation is the #1 reason child applications are delayed.",
    },
  ],
  steps: [
    {
      number: "01",
      icon: "ri-file-list-3-line",
      title: "Complete Your Application",
      description: "Fill out your passport application online or over the phone with our Greater Hartford team. We review everything for accuracy.",
    },
    {
      number: "02",
      icon: "ri-upload-cloud-line",
      title: "Send Us Your Documents",
      description: "Ship your documents via overnight courier to our secure processing center. We confirm receipt and begin review immediately.",
    },
    {
      number: "03",
      icon: "ri-search-line",
      title: "We Review Everything",
      description: "Our agents verify your forms, photos, and supporting documents meet all State Department requirements — no surprises.",
    },
    {
      number: "04",
      icon: "ri-shield-check-line",
      title: "Courier Submission",
      description: "As a registered U.S. Department of State courier, we submit your application with reserved agency processing slots.",
    },
    {
      number: "05",
      icon: "ri-passport-line",
      title: "Receive Your Passport",
      description: "Your passport is delivered securely to your West Hartford address. We provide tracking and daily status updates throughout.",
    },
  ],
  processingOptions: [
    {
      speed: "8-11",
      unit: "weeks",
      label: "Standard Processing",
      tag: "Post Office",
      tagColor: "bg-gray-200 text-gray-600",
      features: ["Mail-in application", "No expedited handling", "No dedicated agent", "No phone or email contact for your case", "Updates only by physical mail letter"],
      highlight: false,
      muted: true,
    },
    {
      speed: "10",
      unit: "business days",
      label: "Smart Saver",
      tag: "Fast Passport Center",
      tagColor: "bg-green-100 text-green-700",
      features: ["Secure overnight shipping included", "Document review before submission", "Dedicated agent assigned", "Daily status updates", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "3-5",
      unit: "business days",
      label: "Expedited Processing",
      tag: "Recommended",
      tagColor: "bg-[#1A365D] text-white",
      features: ["Secure overnight shipping included", "Document review before submission", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "24",
      unit: "hours",
      label: "Emergency Processing",
      tag: "Life or Death",
      tagColor: "bg-red-100 text-red-600",
      features: ["For documented life-or-death emergencies only", "Requires proof of emergency travel", "Strictly for documented life-or-death emergencies"],
      highlight: false,
      emergency: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Must book weeks in advance", fpc: "No appointment needed — we handle agency access" },
    { feature: "Processing Speed", agency: "1-2 business days (if you get an appointment)", fpc: "As fast as 3-5 days or 24 hours for emergencies" },
    { feature: "Document Review", agency: "No pre-check — one mistake means restart", fpc: "Complete review before submission — we catch errors" },
    { feature: "Travel Proof", agency: "Requires printed itinerary with confirmed travel within 2 weeks", fpc: "Flexible documentation requirements" },
    { feature: "Updates", agency: "No phone or email support — only mail updates", fpc: "Daily status updates via phone and email" },
    { feature: "Guarantee", agency: "No guarantee of appointment or same-day processing", fpc: "Reserved agency slots with predictable processing" },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel only with proof of departure date", fpc: "Most U.S. citizens with required documentation" },
    { feature: "Cost vs Resellers", agency: "Government fees only — but you handle all shipping and logistics", fpc: "Best price guarantee — 30-100% less than FedEx, Staples & resellers" },
    { feature: "West Hartford-Specific Guidance", agency: "Generic — no Hartford County or suburban expertise", fpc: "Full West Hartford/Greater Hartford knowledge built in" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "West Hartford", route: "/west-hartford" },
    { name: "Hartford", route: "/hartford" },
    { name: "New Haven", route: "/new-haven" },
    { name: "Bridgeport", route: "/bridgeport" },
    { name: "New Britain", route: "/new-britain" },
    { name: "Bristol", route: "/cities/bristol" },
  ],
  fpcOfficeNote: "Fast Passport Center serves West Hartford and all of Greater Hartford as a remote courier service. We do not have a physical office in West Hartford, but our secure overnight shipping network ensures your documents reach our processing center quickly and safely.",
  fpcOfficeAddress: "850 Canal Street, Stamford, CT 06902",
  fpcOfficeDistance: "Remote courier service — West Hartford, CT area",
  serviceAreaIntro: "Fast Passport Center provides expedited passport services to all West Hartford neighborhoods and surrounding Greater Hartford communities. From West Hartford Center to Elmwood, Farmington to Bloomfield — we serve every Greater Hartford community with the same fast, reliable processing.",
  nearbyCitiesIntro: "We proudly serve West Hartford and all surrounding Greater Hartford communities. Whether you're in West Hartford Center or a nearby suburb, our expedited passport services are available to you with the same speed and reliability.",
  howItWorksIntro: "Our West Hartford customers follow a simple, proven process to get their passports fast. No agency visits, no waiting in lines, no guessing.",
  mistakesIntro: "These are the most common mistakes West Hartford travelers make when applying for an expedited passport. Avoiding them saves weeks of delays.",
  testimonialsIntro: "West Hartford and Greater Hartford travelers trust Fast Passport Center for their urgent passport needs. Here's what they say about our service.",
  faqIntro: "Common questions about expedited passport services in West Hartford, CT. If you don't see your question here, call us at 877-253-0084.",
  statePageRoute: "/states/connecticut",
  statePageName: "Connecticut Passport Services",
  dmvWebsite: "https://portal.ct.gov/DMV",
  dmvLabel: "Connecticut DMV — Official Website",
  vitalRecordsLinks: [
    { icon: "ri-user-heart-line", label: "Birth Certificates — Connecticut DPH", desc: "Request certified Connecticut birth certificates from the Department of Public Health Vital Records Office. Available by mail, online, or in person at the Hartford office.", url: "https://portal.ct.gov/DPH/Vital-Records/Vital-Records-Home" },
    { icon: "ri-file-history-line", label: "Death Certificates — Connecticut DPH", desc: "Obtain certified Connecticut death certificates through the DPH Vital Records Office. Mail, online, and in-person options available.", url: "https://portal.ct.gov/DPH/Vital-Records/Vital-Records-Home" },
    { icon: "ri-heart-pulse-line", label: "Marriage Certificates — West Hartford Town Clerk", desc: "Request certified marriage certificates from the West Hartford Town Clerk's Office. Marriage records are maintained by the town where the ceremony took place.", url: "https://www.westhartfordct.gov/town-clerk" },
    { icon: "ri-scales-line", label: "Divorce Records — Hartford Superior Court", desc: "Obtain certified divorce decrees from the Hartford Judicial District Superior Court, which serves West Hartford. Divorce records are maintained by the court where the decree was granted.", url: "https://jud.ct.gov/" },

  ],
};

export default westHartfordConfig;